import { motion } from "motion/react"
import { useState } from "react"
import { ThemeEvent } from "../types"
import { useBreakpoint } from "@/lib/use-breakpoint"
import posthog from "posthog-js"
import { cn } from "@/lib/utils"
import DiscardDialog from "./DiscardDialog"
import { Trash2 } from "lucide-react"

type Props = {
  to: "story" | "result"
  data: ThemeEvent,
  onThemeSelect: (id: number, nav: string) => void,
  onReasoning: (name: string, why: string) => void
}

const ThemeCard = ({ data, onThemeSelect, onReasoning }: Props) => {

  const [isHovered, setIsHovered] = useState(false)

  const [isDeleting, setIsDeleting] = useState(false)

  const size = (window.innerWidth < 1280 && window.innerWidth > 1024) ? 40 : 100

  const breakpoint = useBreakpoint();

  const getWidth = () => {
    if (breakpoint === "mobile") {
      return isHovered ? "4rem" : "3rem";
    }
    if (breakpoint === "tablet") {
      return isHovered ? "5rem" : "4rem";
    }
    return isHovered ? "6rem" : "5rem"; // desktop
  };

  return (
    <div className={cn("p-[2px] overflow-hidden bg-gradient-to-tr bg-[size:200%_200%] animate-wave duration-700 rounded-3xl",
      data.viewed ? "from-purple-300 via-pink-400 to-fuchsia-600" : "from-purple-400 via-indigo-500 to-pink-600"
    )}>
      <motion.div
        onClick={() => {
          if (!isDeleting) {
            onThemeSelect(data.event_id, data.stories === true ? "story" : "result")
            posthog.capture('theme viewed', { event_id: data.event_id })
          }
        }}
        className="flex-1 h-32 rounded-3xl p-2 text-white shadow-xl cursor-pointer relative bg-white"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="absolute inset-4 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <motion.div
                className="flex-shrink-0 overflow-hidden w-20"
                animate={{
                  width: getWidth(),
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <img
                  src={data.image_url}
                  alt={data.event_name}
                  className="w-full h-full object-cover rounded-lg shadow-lg"
                />
              </motion.div>
              <div className="flex-1 min-w-0">
                <motion.h2
                  initial={false}
                  animate={{
                    fontSize: isHovered ? "1.3rem" : "1rem",
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                  className={cn("text-base lg:text-lg font-bold lg:leading-[1.2rem] tracking-wide",
                    data.viewed ? "text-pink-500" : "text-purple-600"
                  )}>{data.event_name}</motion.h2>
                <motion.p
                  className="text-[0.7rem] lg:text-xs text-gray-500"
                  animate={{
                    opacity: isHovered ? 0 : 1,
                    y: isHovered ? -10 : 0,
                  }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  {data.reasoning.slice(0, size)}...
                </motion.p>
              </div>
            </div>
            {/* <div className={cn("rounded-xl w-9 sm:w-12 flex items-center justify-center gap-0 flex-col aspect-square shadow-lg ml-2 border border-purple-300",
              data.viewed ? "bg-pink-500 text-white border-pink-500" : "bg-white text-indigo-600"
            )}>

            <span className="text-xs sm:text-sm font-bold h-4">{getTrueDate(data.start_date, "MMM")}</span>
            <span className="text-base sm:text-xl font-bold ">{getTrueDate(data.start_date, "d")}</span>
          </div> */}
          </div>

          <DiscardDialog
            id={data.event_id}
            onDelete={() => setIsDeleting(false)}
            trigger={

              <motion.button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsDeleting(true)
                }}
                initial={{ opacity: 0, y: 8 }}
                animate={{
                  opacity: isHovered ? 1 : 0,
                  y: isHovered ? 0 : 8,
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                  delay: isHovered ? 0.1 : 0,
                }}
                className="flex justify-end absolute bottom-0 left-24 z-30 text-sm underline text-black rounded-full bg-white p-2 cursor-pointer"
              >
                <Trash2 size={12} color="red" />
              </motion.button>
            }
          />
          <motion.button
            onClick={(e) => {
              e.stopPropagation()
              onReasoning(data.event_name, data.reasoning)
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: isHovered ? 1 : 0,
              y: isHovered ? 0 : 8,
            }}
            transition={{
              duration: 0.4,
              ease: "easeOut",
              delay: isHovered ? 0.1 : 0,
            }}
            className="flex justify-end absolute bottom-0 right-2 text-sm underline text-black underline-offset-4 cursor-pointer"
          >
            Why this idea?
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}

export default ThemeCard
